import LiquidText, { liquidTone } from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { abrirFicheiroDaResposta } from '../utils/abrirFicheiro';

/* ============================================================
   PAINEL DE PREPARAÇÃO DA AUDITORIA

   A lista que a auditora deixou, ponto a ponto, com o estado, o
   que foi feito, onde está na aplicação e a evidência a abrir
   num clique. Serve dois momentos: a auditora a consultar por
   si, e a SPROD a responder ao vivo durante a auditoria.

   A coluna da auditora é separada da da SPROD de propósito —
   vê-se quem disse o quê.
   ============================================================ */

const ESTADOS = {
    fechado: { label: 'Fechado', bg: '#dcfce7', fg: '#166534', ordem: 3 },
    parcial: { label: 'Parcial', bg: '#fef9c3', fg: '#854d0e', ordem: 2 },
    aberto: { label: 'Aberto', bg: '#fee2e2', fg: '#991b1b', ordem: 1 },
    terceiros: { label: 'Depende de terceiros', bg: '#e0f2fe', fg: '#075985', ordem: 1 },
};

const fmtData = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return ''; } };

export default function PontosAuditora() {
    const [pontos, setPontos] = useState([]);
    const [resumo, setResumo] = useState({});
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('todos');
    const [aberto, setAberto] = useState(null);
    const [abrindo, setAbrindo] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/pontos-auditora');
            setPontos(r.data.pontos || []);
            setResumo(r.data.resumo || {});
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    /* Os ficheiros servem-se pelo endpoint autenticado da procura — o mesmo
       que a lupa usa. Um window.open direto devolvia 401. */
    const abrirEvidencia = async (ev) => {
        const chave = `${ev.fonte}-${ev.id}`;
        setAbrindo(chave);
        try {
            const r = await api.get(`/procura-sgi/ficheiro/${ev.fonte}/${ev.id}`, { responseType: 'blob' });
            abrirFicheiroDaResposta(r, ev.titulo || '');
        } catch { alert('Erro ao abrir o documento.'); }
        finally { setAbrindo(null); }
    };

    const marcarVerificado = async (p, verificado, observacoes) => {
        try {
            await api.put(`/pontos-auditora/${p.id}/verificacao`, { verificado, observacoes });
            carregar();
        } catch { alert('Erro ao registar a verificação.'); }
    };

    const visiveis = pontos.filter(p => filtro === 'todos'
        || (filtro === 'porfechar' ? p.estado !== 'fechado' : p.estado === filtro));

    return (
        <div>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '1.05rem', color: '#1e293b' }}><LiquidText>
                    📋 Lista da auditora — estado de cada ponto
                </LiquidText></h3>
                <p style={{ margin: 0, fontSize: '0.84rem', color: '#64748b' }}>
                    Cada linha é um ponto que a auditora identificou. O estado e a evidência são o que
                    está na aplicação hoje; a coluna da auditora é dela para preencher à medida que
                    verifica.
                </p>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
                <div className="shared-subnav audit-filters">{[['todos', `Todos (${resumo.total || 0})`],
                ['porfechar', `Por fechar (${(resumo.total || 0) - (resumo.fechado || 0)})`],
                ['fechado', `Fechados (${resumo.fechado || 0})`],
                ['parcial', `Parciais (${resumo.parcial || 0})`],
                ['aberto', `Abertos (${resumo.aberto || 0})`],
                ['terceiros', `Terceiros (${resumo.terceiros || 0})`]].map(([k, label]) => (
                    <button className={`shared-subnav-btn ${filtro === k ? 'active' : ''}`} key={k} aria-pressed={filtro === k} onClick={() => setFiltro(k)}
                        style={{
                            padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: '0.82rem',
                            fontWeight: 600, border: `1px solid ${filtro === k ? '#1e293b' : '#cbd5e1'}`,
                            background: filtro === k ? '#1e293b' : '#fff',
                            color: filtro === k ? '#fff' : '#475569',
                        }}><LiquidText>{label}</LiquidText></button>
                ))}</div>
                <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#64748b' }}>
                    {resumo.verificados || 0} de {resumo.total || 0} verificados pela auditora
                </span>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <p style={{ color: '#94a3b8', padding: '30px 0', textAlign: 'center' }}>Nada nesta vista.</p>
                ) : <div className="audit-list-wrap"><table className="standard-table audit-list"><thead><tr><th>N.º</th><th>Ponto / Evidência</th><th>Estado</th><th>Verificação da auditora</th><th>Ações</th></tr></thead><tbody>{visiveis.map(p => {
                    const es = ESTADOS[p.estado] || ESTADOS.aberto;
                    const expandido = aberto === p.id;
                    const evid = Array.isArray(p.evidencias) ? p.evidencias : [];
                    return (
                        <React.Fragment key={p.id}>
                            <tr>
                                <td className="audit-number">{p.numero}</td>
                                <td className="audit-point">
                                    <button className="audit-point-title" aria-expanded={expandido} aria-controls={`audit-detail-${p.id}`} onClick={() => setAberto(expandido ? null : p.id)}>{p.ponto}</button>
                                    {p.o_que_foi_feito && <div className="audit-point-summary">{p.o_que_foi_feito.slice(0, 150)}{p.o_que_foi_feito.length > 150 ? '…' : ''}</div>}
                                </td>
                                <td><span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true">{es.label}</span></td>
                                <td><span data-liquid-tone={p.verificado_pela_auditora ? 'success' : 'neutral'} data-liquid-badge="true">{p.verificado_pela_auditora ? 'Verificado' : 'Por verificar'}</span>{p.data_verificacao && <small className="audit-verification-date">{fmtData(p.data_verificacao)}</small>}</td>
                                <td><button data-liquid-secondary="true" className="audit-detail-toggle" aria-label={`${expandido ? 'Fechar' : 'Abrir'} ponto ${p.numero}`} aria-expanded={expandido} aria-controls={`audit-detail-${p.id}`} onClick={() => setAberto(expandido ? null : p.id)}><OrcamentoIcon name={expandido ? 'up' : 'down'} fallback={expandido ? '↑' : '↓'} />{expandido ? 'Fechar' : 'Detalhes'}</button></td>
                            </tr>

                            {expandido && (
                                <tr className="audit-detail-row" id={`audit-detail-${p.id}`}><td colSpan={5}><div className="audit-detail-content">
                                    {p.o_que_foi_feito && (
                                        <div style={{ marginTop: 12 }}>
                                            <div style={rot}>O que foi feito</div>
                                            <div style={{ fontSize: '0.86rem', color: '#334155', whiteSpace: 'pre-line' }}>{p.o_que_foi_feito}</div>
                                        </div>
                                    )}
                                    {p.onde_ver && (
                                        <div style={{ marginTop: 12 }}>
                                            <div style={rot}>Onde ver na aplicação</div>
                                            <div data-liquid-notice="true" data-liquid-tone={liquidTone('#eff6ff')} style={{
                                                display: 'inline-block', background: '#eff6ff', color: '#1d4ed8',
                                                padding: '4px 12px', borderRadius: 8, fontSize: '0.84rem', fontWeight: 600,
                                            }}>{p.onde_ver}</div>
                                        </div>
                                    )}
                                    {evid.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            <div style={rot}>Evidência</div>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {evid.map((ev, i) => (
                                                    <button key={i} onClick={() => abrirEvidencia(ev)}
                                                        disabled={abrindo === `${ev.fonte}-${ev.id}`}
                                                        style={{
                                                            padding: '6px 13px', border: '1px solid #3182ce', background: '#fff',
                                                            color: '#1d4ed8', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem',
                                                        }}><LiquidText>
                                                        📄 </LiquidText>{abrindo === `${ev.fonte}-${ev.id}` ? 'A abrir…' : ev.titulo}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {/* Num ponto fechado já não há nada por fazer: o texto
                                        fica guardado, mas não se mostra como pendente. */}
                                    {p.por_fazer && p.estado !== 'fechado' && (
                                        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fffbeb')} style={{ marginTop: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                                            <div style={{ ...rot, color: '#92400e' }}>Por fazer</div>
                                            <div style={{ fontSize: '0.86rem', color: '#92400e', whiteSpace: 'pre-line' }}>{p.por_fazer}</div>
                                        </div>
                                    )}

                                    <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                                        <div style={rot}>Verificação da auditora</div>
                                        <textarea defaultValue={p.observacoes_auditora || ''}
                                            placeholder="Observações da auditora sobre este ponto…"
                                            onBlur={e => {
                                                if (e.target.value !== (p.observacoes_auditora || ''))
                                                    marcarVerificado(p, p.verificado_pela_auditora, e.target.value);
                                            }}
                                            rows={2}
                                            style={{
                                                width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1',
                                                borderRadius: 8, fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box',
                                            }} />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={!!p.verificado_pela_auditora}
                                                onChange={e => marcarVerificado(p, e.target.checked, p.observacoes_auditora)} />
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                                Verificado pela auditora
                                                {p.data_verificacao ? ` em ${fmtData(p.data_verificacao)}` : ''}
                                            </span>
                                        </label>
                                    </div>
                                </div></td></tr>
                            )}
                        </React.Fragment>
                    );
                })}</tbody></table></div>}
        </div>
    );
}

const rot = {
    fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.04em', marginBottom: 4,
};
